<!--
  The unread dot is a sibling of the button, not a child: `flxButton` projects an icon-only button's
  content through `<ng-content select="flx-icon,[flxIcon]" />` alone, so anything else inside is
  dropped. It matches the design, where the counter sits beside the button rather than in it.
-->
<div class="relative flex">
  <button
    (click)="panel.toggle($event)"
    class="size-7 rounded-lg border-0 text-white hover:bg-white/10"
    variant="ghost"
    flxButton
    icon="center"
    type="button"
    data-testid="notifications-bell-button"
  >
    <i
      title="Notifications"
      class="ph ph-bell-simple text-base"
      flxIcon
    ></i>
  </button>

  @if (unreadCount() > 0) {
    <span
      class="pointer-events-none absolute right-1.5 top-1.5 size-2 rounded-full bg-red-500"
      data-testid="notifications-bell-badge"
    ></span>
  }
</div>

<p-popover
  #panel
  (onShow)="onPanelShow()"
  (onHide)="onPanelHide()"
  styleClass="flx-prime-popover content-unpadded notifications-center-popover"
>
  <ng-template #content>
    <div
      class="flex w-[420px] flex-col"
      data-testid="notifications-center-panel"
    >
      <div class="flex flex-col gap-2 border-b border-neutral-100 py-3">
        <div class="flex items-start justify-between gap-2 px-6">
          <span class="text-lg/7 font-bold text-neutral-800">Notification</span>

          <button
            (click)="markAllAsRead()"
            class="flx-ds ternary small"
            flxButton
            type="button"
            data-testid="notifications-mark-all-as-read-button"
          >
            Mark all as read
          </button>
        </div>

        <div class="flex items-start gap-2 px-6">
          <flx-input class="flx-ds flex-1">
            <input
              [value]="search()"
              (input)="onSearchInput($event)"
              placeholder="Search notifications..."
              flxInput
              data-testid="notifications-search-input"
            />
            <i
              class="ph ph-magnifying-glass text-2xl/6 text-neutral-500"
              flxSuffix
            ></i>
          </flx-input>

          <!--
            The dropdown is rendered inline rather than in a second `p-popover`: an overlay appended
            to the body counts as an outside click and would dismiss the panel it sits in.
          -->
          <div class="relative">
            <button
              [class]="{ '!border-blue-100': isFiltering(), '!bg-blue-50': isFiltering() }"
              (click)="filterOpen.set(!filterOpen())"
              class="flx-ds secondary"
              flxButton
              icon="center"
              type="button"
              data-testid="notifications-filter-button"
            >
              <i
                [class.text-blue-500]="isFiltering()"
                title="Filter notifications"
                class="ph ph-funnel-simple"
                flxIcon
              ></i>
            </button>

            @if (filterOpen()) {
              <div
                class="absolute right-0 top-full z-10 mt-1 flex w-[252px] flex-col gap-1 rounded-lg bg-white p-2 shadow-[2px_2px_12px_rgba(22,52,98,0.12)]"
                data-testid="notifications-filter-dropdown"
              >
                <button
                  [class]="
                    filter() === NotificationFilterMode.ALL
                      ? 'bg-blue-50 text-blue-500'
                      : 'text-black'
                  "
                  (click)="selectFilter(NotificationFilterMode.ALL)"
                  class="flex items-center rounded p-1 text-left text-xs/4"
                  type="button"
                  data-testid="notifications-filter-option-ALL"
                >
                  All Notifications
                </button>

                <button
                  [class]="
                    filter() === NotificationFilterMode.UNREAD
                      ? 'bg-blue-50 text-blue-500'
                      : 'text-black'
                  "
                  (click)="selectFilter(NotificationFilterMode.UNREAD)"
                  class="flex items-center gap-2 rounded p-1 text-left text-xs/4"
                  type="button"
                  data-testid="notifications-filter-option-UNREAD"
                >
                  <span class="flex-1">Unread</span>
                  <span
                    class="text-blue-300"
                    data-testid="notifications-filter-unread-count"
                  >
                    {{ unreadCount() }}
                  </span>
                </button>

                <div class="my-1 border-t border-neutral-100"></div>

                <span class="p-1 text-xs/4 font-semibold text-neutral-400">Workspace Type</span>

                @for (workspaceType of WORKSPACE_TYPES; track workspaceType) {
                  <button
                    [class.bg-blue-50]="filter() === workspaceType"
                    [attr.data-testid]="'notifications-filter-option-' + workspaceType"
                    (click)="selectFilter(workspaceType)"
                    class="flex items-center rounded p-1"
                    type="button"
                  >
                    <flx-workspace-env-type-tag
                      [envType]="workspaceType"
                      [name]="workspaceType"
                    />
                  </button>
                }
              </div>
            }
          </div>
        </div>
      </div>

      <div class="flex h-[395px] flex-col overflow-y-auto">
        @if (loading()) {
          <div
            class="flex grow items-center justify-center py-6"
            data-testid="notifications-center-loading"
          >
            <flx-spinner size="small" />
          </div>
        } @else if (!groups().length) {
          <div class="flex grow items-center justify-center px-6 py-6 text-center">
            <p
              class="text-xs text-neutral-500"
              data-testid="notifications-center-empty"
            >
              {{ emptyMessage() }}
            </p>
          </div>
        } @else {
          @for (group of groups(); track group.period) {
            <div
              class="mt-3 px-6 pb-1 pt-1.5 text-xs/[18px] font-semibold text-neutral-400 first:mt-0"
              data-testid="notifications-center-group"
            >
              {{ group.period }}
            </div>

            @for (notification of group.notifications; track notification.id) {
              <flx-notification-center-item
                [notification]="notification"
                [currentTime]="currentTime()"
                (markAsRead)="markAsRead(notification.id)"
                (actionClicked)="onActionClicked(notification); panel.hide()"
              />
            }
          }
        }
      </div>

      <div
        class="flex h-7 shrink-0 items-center justify-center gap-1 border-t border-neutral-100 bg-neutral-50 px-4 text-xs/[18px] text-neutral-300"
        data-testid="notifications-center-footer"
      >
        <i class="ph ph-clock text-xs/[12px]"></i>
        Notifications are kept for {{ NOTIFICATION_RETENTION_DAYS }} days
      </div>
    </div>
  </ng-template>
</p-popover>
